import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  Grid,
  Avatar,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BedIcon from "@mui/icons-material/Bed";
import KitchenIcon from "@mui/icons-material/Kitchen";
import BathtubIcon from "@mui/icons-material/Bathtub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PetsIcon from "@mui/icons-material/Pets";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import StairsIcon from "@mui/icons-material/Stairs";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import PoolIcon from "@mui/icons-material/Pool";
import ChairIcon from "@mui/icons-material/Chair";
import HotTubIcon from "@mui/icons-material/HotTub";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SecurityIcon from "@mui/icons-material/Security";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import NoteIcon from "@mui/icons-material/Note";
import cardPic from "../../../assets/card_pic.webp";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./flatDetailModal.css";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import GavelIcon from "@mui/icons-material/Gavel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PropertyRulesPanel, {
  rulesReportSections,
} from "../../../components/PropertyRulesPanel";
import { usePropertyRule } from "../../../utils/propertyRules";
import { openPropertyReport, reportMoney } from "../../../utils/propertyReport";
import {
  evaluateTenantEligibility,
  tenantProfileFromUser,
  allowedForLabel,
} from "../../../utils/tenantEligibility";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import BlockIcon from "@mui/icons-material/Block";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { isPastDueDate } from "../../../utils/dueDate";
import { getChargeAmounts } from "../../../utils/chargeHelper";
import { resolveLatePolicy, describeLatePolicy } from "../../../utils/latePolicy";

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "950px" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  overflow: "hidden",
};

// Custom Arrow component for slider
const CustomArrow = ({ onClick, direction }) => (
  <div
    className={`modal-slider-arrow ${direction}`}
    onClick={onClick}
    role="button"
  />
);

// Helper function for ordinal numbers
const getOrdinal = (num) => {
  // if (!num) return "";
  // if (num % 100 >= 11 && num % 100 <= 13) return `${num}th`;
  // switch (num % 10) {
  //   case 1:
  //     return `${num}st`;
  //   case 2:
  //     return `${num}nd`;
  //   case 3:
  //     return `${num}rd`;
  //   default:
  //     return `${num}th`;
  // }
  if (num === 0) return "Ground";
  if (num % 100 >= 11 && num % 100 <= 13) {
    return `${num}th`;
  }
  switch (num % 10) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

export default function FlatDetailModal({
  open,
  onClose,
  flat,
  landlordName,
  landlordEmail,
  landlordPhone,
  landlordId,
  onInterested,
  requestStatus,
  request,
}) {
  const navigate = useNavigate();
  const { validateUser } = useSelector((state) => state);

  // Must sit above the `!flat` bail-out — hooks cannot run conditionally.
  const { rules, loading: rulesLoading } = usePropertyRule(
    landlordId,
    flat?._id || flat?.id || flat?.flatId
  );

  if (!flat) return null;

  const latePolicy = resolveLatePolicy(flat);

  // Owner's property data vs the tenant's own profile — one shared rule-set, so
  // the card, this modal and the request form can never disagree.
  const eligibility = evaluateTenantEligibility(
    flat,
    tenantProfileFromUser(validateUser)
  );
  const isDisabled = !eligibility.canRequest;
  const disabledReason = eligibility.tooltip;
  const images = flat.images?.length > 0 ? flat.images : [cardPic];

  const sliderSettings = {
    dots: true,
    infinite: images.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: images.length > 1,
    autoplaySpeed: 4000,
    nextArrow: <CustomArrow direction="next" />,
    prevArrow: <CustomArrow direction="prev" />,
    dotsClass: "slick-dots modal-slider-dots",
  };

  const handleInterested = () => {
    // The button is disabled in this state; this guard keeps a stray programmatic
    // call from opening the request form anyway.
    if (!eligibility.canRequest) return;
    onInterested({
      flat,
      landlordId,
      landlordName,
      landlordEmail,
    });
  };

  const yesNo = (value) =>
    value === true || value === "true" || value === 1 ? "Yes" : "No";

  // Everything the modal shows plus everything it has no room for, in one
  // saveable document — the card stays a summary precisely because this exists.
  const handleDownloadReport = () => {
    const opened = openPropertyReport({
      title: flat.flatName || "Property",
      subtitle:
        [flat.propertyAddress, flat.area, flat.city].filter(Boolean).join(", ") ||
        "Location not specified",
      accent: "#1976d2",
      stats: [
        { label: "Monthly rent", value: reportMoney(flat.flatRent) },
        { label: "Advance", value: reportMoney(flat.flatAdvance) },
        {
          label: "Layout",
          value: `${flat.flatRooms || 0} bed • ${flat.flatToilet || 0} bath`,
        },
        {
          label: "Availability",
          value: flat.is_rent === 1 ? "Rented" : "Available",
        },
      ],
      sections: [
        {
          heading: "Property",
          rows: [
            { label: "Name", value: flat.flatName },
            { label: "Floor", value: getOrdinal(flat.flatFloor) },
            { label: "Bedrooms", value: flat.flatRooms },
            { label: "Bathrooms", value: flat.flatToilet },
            { label: "Kitchens", value: flat.flatKitchen },
            { label: "Allowed for", value: flat.allowedFor },
            { label: "City", value: flat.city },
            { label: "Area", value: flat.area },
          ],
        },
        {
          heading: "Costs",
          rows: [
            { label: "Monthly rent", value: reportMoney(flat.flatRent) },
            { label: "Advance", value: reportMoney(flat.flatAdvance) },
            {
              label: "Security charges",
              value: reportMoney(getChargeAmounts(flat).security),
            },
            {
              label: "Maintenance charges",
              value: reportMoney(getChargeAmounts(flat).maintenance),
            },
            {
              label: "Trash charges",
              value: reportMoney(getChargeAmounts(flat).trash),
            },
            {
              label: "Water charges",
              value: reportMoney(getChargeAmounts(flat).water),
            },
            {
              label: "Late payment policy",
              value: describeLatePolicy(resolveLatePolicy(flat)) || "No late fee",
            },
          ],
        },
        {
          heading: "Facilities",
          rows: [
            { label: "Pets allowed", value: yesNo(flat.petAllowed) },
            { label: "Parking", value: yesNo(flat.parkingAvailable) },
            { label: "Furnished", value: yesNo(flat.furnished) },
            { label: "Swimming pool", value: yesNo(flat.pool) },
            { label: "Bath tub", value: yesNo(flat.bathTub) },
            { label: "Balcony", value: yesNo(flat.balcony) },
            { label: "Elevator", value: yesNo(flat.elevator) },
            { label: "WiFi", value: yesNo(flat.wifi) },
            { label: "Gym", value: yesNo(flat.gym) },
            { label: "Smoke alarm", value: yesNo(flat.smokeAlarm) },
            { label: "Fire extinguisher", value: yesNo(flat.fireExtinguisher) },
            { label: "First aid kit", value: yesNo(flat.firstAidKit) },
          ],
        },
        ...rulesReportSections(rules),
        {
          heading: "Property owner",
          rows: [
            { label: "Name", value: landlordName || "—" },
            { label: "Email", value: landlordEmail || "—" },
            { label: "Phone", value: landlordPhone || "—" },
          ],
        },
      ],
    });

    if (!opened) {
      window.alert(
        "Please allow pop-ups for this site to download the property report."
      );
    }
  };

  // Amenities/Facilities list
  const amenities = [
    {
      label: "Pets Allowed",
      icon: <PetsIcon />,
      active: flat.petAllowed === "true" || flat.petAllowed === true,
    },
    {
      label: "Parking",
      icon: <LocalParkingIcon />,
      active:
        flat.parkingAvailable === "true" || flat.parkingAvailable === true,
    },
    {
      label: "Furnished",
      icon: <ChairIcon />,
      active: flat.furnished === "true" || flat.furnished === true,
    },
    {
      label: "Swimming Pool",
      icon: <PoolIcon />,
      active: flat.pool === "true" || flat.pool === true,
    },
    {
      label: "Bath Tub",
      icon: <HotTubIcon />,
      active: flat.bathTub === "true" || flat.bathTub === true,
    },
  ];

  const activeAmenities = amenities.filter((a) => a.active);

  // Parse tags

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ApartmentIcon />
            <Typography variant="h5" fontWeight={600}>
              {flat.flatName || "Property Details"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadReport}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                textTransform: "none",
                fontWeight: 600,
                color: "white",
                borderRadius: 2,
                px: 1.6,
                bgcolor: "rgba(255,255,255,0.16)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
              }}
            >
              Full Report
            </Button>
            <Tooltip title="Download full report">
              <IconButton
                onClick={handleDownloadReport}
                sx={{ color: "white", display: { xs: "inline-flex", sm: "none" } }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content - Scrollable */}
        <Box sx={{ maxHeight: "calc(90vh - 160px)", overflowY: "auto" }}>
          {/* Image Slider */}
          <Box className="modal-image-slider" sx={{ position: "relative" }}>
            <Slider {...sliderSettings}>
              {images.map((img, idx) => (
                <Box key={idx}>
                  <img
                    src={img}
                    alt={`Property ${idx + 1}`}
                    style={{
                      width: "100%",
                      height: "320px",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Slider>

            {/* Price Badge */}
            <Box
              sx={{
                position: "absolute",
                top: 15,
                right: 15,
                bgcolor: "rgba(25, 118, 210, 0.95)",
                color: "white",
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                zIndex: 10,
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Monthly Rent
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                Rs {Number(flat.flatRent || 0).toLocaleString()}
              </Typography>
            </Box>

            {/* Status Badge */}
            <Box sx={{ position: "absolute", top: 15, left: 15, zIndex: 10 }}>
              <Chip
                label={flat.is_rent === 1 ? "Rented" : "Available"}
                color={flat.is_rent === 1 ? "error" : "success"}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 3 }}>
            {/* Why this tenant cannot request — spelled out, never just a
                greyed-out button the visitor has to guess about. */}
            {eligibility.blockers.length > 0 && (
              <Alert
                severity="error"
                icon={<BlockIcon />}
                sx={{ mb: 3, borderRadius: 2, alignItems: "flex-start" }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>
                  You can view this property, but you cannot send a request
                </AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {eligibility.blockers.map((rule) => (
                    <li key={rule.key}>
                      <Typography variant="body2" component="span">
                        <strong>{rule.title}:</strong> {rule.reason}
                      </Typography>
                      {rule.fix && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {rule.fix}
                        </Typography>
                      )}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {eligibility.canRequest && eligibility.warnings.length > 0 && (
              <Alert
                severity="warning"
                icon={<InfoOutlinedIcon />}
                sx={{ mb: 3, borderRadius: 2, alignItems: "flex-start" }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>
                  Worth checking before you request
                </AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {eligibility.warnings.map((rule) => (
                    <li key={rule.key}>
                      <Typography variant="body2" component="span">
                        {rule.reason}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {/* Property Name & Location */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {flat.flatName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                }}
              >
                <LocationOnIcon fontSize="large" />
                <Typography variant="body1">
                  {[flat.propertyAddress].filter(Boolean).join(", ") ||
                    "Location not specified"}
                </Typography>
              </Box>
            </Box>

            {/* Tags */}
            {/* {tags.length > 0 && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                {tags.map((tag, idx) => (
                  <Chip
                    key={idx}
                    icon={<LocalOfferIcon />}
                    label={tag.trim()}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            )} */}

            {/* Quick Info Chips */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              {allowedForLabel(flat.allowedFor) && (
                <Chip
                  style={{ padding: "5px" }}
                  icon={<FamilyRestroomIcon fontSize="small" />}
                  label={`${allowedForLabel(flat.allowedFor)} Allowed`}
                  color={
                    eligibility.blockers.some(
                      (rule) => rule.key === "tenantType"
                    )
                      ? "error"
                      : "success"
                  }
                />
              )}
              {flat.flatNumber && (
                <Chip
                  style={{ padding: "5px" }}
                  icon={<HomeIcon fontSize="small" />}
                  label={`Flat #${flat.flatNumber}`}
                  variant="outlined"
                />
              )}
            </Box>

            {/* Property Specs - Main Grid */}
            <Paper
              elevation={0}
              sx={{
                background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
                p: 2.5,
                borderRadius: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, color: "#1976d2" }}
              >
                Property Specifications
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
                      <BedIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Bedrooms
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {flat.flatRooms || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
                      <BathtubIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Bathrooms
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {flat.flatToilet || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
                      <KitchenIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Kitchen
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {flat.flatKitchen || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
                      <StairsIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Floor
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {getOrdinal(flat.flatFloor) || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {flat.personAllowed && (
                  <Grid item xs={6} sm={4} md={3}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
                        <PeopleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Max Persons
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {flat.personAllowed}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Pricing Details */}
            <Paper
              elevation={0}
              sx={{
                background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                p: 2.5,
                borderRadius: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, color: "#2e7d32" }}
              >
                Pricing Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#c8e6c9", color: "#2e7d32" }}>
                      <AccountBalanceWalletIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Rent
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        Rs {Number(flat.flatRent || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {flat.flatAdvance > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#c8e6c9", color: "#2e7d32" }}>
                        <ReceiptLongIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Advance
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          Rs {Number(flat.flatAdvance).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {getChargeAmounts(flat).security > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#c8e6c9", color: "#2e7d32" }}>
                        <SecurityIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Security
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          Rs {getChargeAmounts(flat).security.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {getChargeAmounts(flat).maintenance > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00" }}>
                        <ReceiptLongIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Maintenance
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          Rs{" "}
                          {getChargeAmounts(flat).maintenance.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {getChargeAmounts(flat).trash > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#fff3e0", color: "#ef6c00" }}>
                        <DeleteOutlineIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Trash Charges
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          Rs {getChargeAmounts(flat).trash.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {getChargeAmounts(flat).water > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar sx={{ bgcolor: "#e1f5fe", color: "#0277bd" }}>
                        <ReceiptLongIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Water Charges
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          Rs {getChargeAmounts(flat).water.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Late Payment Policy */}
            {(latePolicy.enabled || latePolicy.gracePeriodDays > 0) && (
              <Paper
                elevation={0}
                sx={{
                  background:
                    "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                  p: 2.5,
                  borderRadius: 3,
                  mb: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 2, color: "#ef6c00" }}
                >
                  Payment Policy
                </Typography>
                <Grid container spacing={2}>
                  {latePolicy.gracePeriodDays > 0 && (
                    <Grid item xs={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar sx={{ bgcolor: "#ffe0b2", color: "#ef6c00" }}>
                          <EventAvailableIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Grace Period
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {latePolicy.gracePeriodDays} Days
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {latePolicy.enabled && (
                    <Grid item xs={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar sx={{ bgcolor: "#ffcdd2", color: "#c62828" }}>
                          <AccessTimeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {latePolicy.type === "PER_DAY_FIXED" && "Late Fee/Day"}
                            {latePolicy.type === "PER_DAY_PERCENTAGE" && "Late Fee/Day (% of rent)"}
                            {latePolicy.type === "FIXED_AFTER_GRACE" && "Late Fee (one-time)"}
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {latePolicy.type === "PER_DAY_PERCENTAGE"
                              ? `${latePolicy.amount}%`
                              : `Rs ${Number(latePolicy.amount).toLocaleString()}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {latePolicy.enabled &&
                    latePolicy.type !== "FIXED_AFTER_GRACE" &&
                    (latePolicy.maximumAmount || latePolicy.maximumPercentage) && (
                      <Grid item xs={6}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                          <Avatar sx={{ bgcolor: "#ffcdd2", color: "#c62828" }}>
                            <AccessTimeIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Maximum Late Fee
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {latePolicy.type === "PER_DAY_PERCENTAGE"
                                ? `${latePolicy.maximumPercentage}% of rent`
                                : `Rs ${Number(latePolicy.maximumAmount).toLocaleString()}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                </Grid>
              </Paper>
            )}

            {/* Amenities */}
            {activeAmenities.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Amenities & Features
                </Typography>
                <Grid container spacing={1.5}>
                  {activeAmenities.map((amenity, idx) => (
                    <Grid item key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          border: "1px solid rgba(76, 175, 80, 0.3)",
                          color: "#2e7d32",
                        }}
                      >
                        {amenity.icon}
                        <Typography variant="body2" fontWeight={600}>
                          {amenity.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Note from Landlord */}
            {flat.note && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  mb: 3,
                  bgcolor: "#fafafa",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <NoteIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Note from Landlord
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  "{flat.note}"
                </Typography>
              </Paper>
            )}

            {/* Property Location Map */}
            {(() => {
              const lat = Number(flat.lat) || Number(flat.latitude);
              const lng =
                Number(flat.lng) || Number(flat.longitude) || Number(flat.lon);
              const hasValidCoordinates =
                lat && lng && !isNaN(lat) && !isNaN(lng);
              const position = hasValidCoordinates
                ? [lat, lng]
                : [24.8607, 67.0011];
              const address =
                flat.propertyAddress ||
                flat.mapAddress ||
                flat.address ||
                "Address not available";

              return (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    mb: 3,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 2,
                      bgcolor: "#f5f5f5",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <MapIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Property Location
                    </Typography>
                  </Box>

                  {hasValidCoordinates ? (
                    <Box sx={{ height: "250px", width: "100%" }}>
                      <MapContainer
                        center={position}
                        zoom={16}
                        style={{ height: "100%", width: "100%" }}
                        scrollWheelZoom={true}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={position}>
                          <Popup>
                            <Box sx={{ maxWidth: 200 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {flat.flatName || "Property"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {address}
                              </Typography>
                            </Box>
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: "150px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Map location not available for this property
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fafafa",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <LocationOnIcon
                          color="primary"
                          sx={{ mt: 0.3, fontSize: 20 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {address}
                          </Typography>
                          {hasValidCoordinates && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Coordinates: {lat.toFixed(6)}, {lng.toFixed(6)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {hasValidCoordinates && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={() => {
                            window.open(
                              `https://www.google.com/maps?q=${lat},${lng}`,
                              "_blank"
                            );
                          }}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            minWidth: "auto",
                          }}
                        >
                          Open in Google Maps
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })()}

            <Divider sx={{ my: 3 }} />

            {/* Termination & violation rule-set. The single most consequential
                thing a tenant signs up to, and previously not visible anywhere
                until after the agreement already existed. */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid #e8ecf3",
                bgcolor: "#fcfdff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <GavelIcon sx={{ color: "#1976d2" }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Rules &amp; Exit Terms
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                What happens if either side ends the agreement early, and what
                counts as an immediate breach.
              </Typography>

              <PropertyRulesPanel
                rules={rules}
                loading={rulesLoading}
                accent="#1976d2"
              />
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* Landlord Info */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, color: "#7b1fa2" }}
              >
                Property Owner
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "#7b1fa2",
                    fontSize: "1.5rem",
                  }}
                >
                  {landlordName ? (
                    landlordName.charAt(0).toUpperCase()
                  ) : (
                    <PersonIcon />
                  )}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {landlordName || "Property Owner"}
                  </Typography>
                  {landlordEmail && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {landlordEmail}
                      </Typography>
                    </Box>
                  )}
                  {/* {landlordPhone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {landlordPhone.substring(0, 7)}{"..."}
                      </Typography>
                    </Box>
                  )} */}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Footer - Fixed */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#fafafa",
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Cost
            </Typography>
            <Typography variant="h5" color="primary" fontWeight={700}>
              Rs{" "}
              {Number(
                (flat.flatRent || 0) + (flat.flatAdvance || 0)
                // (flat.flatSecurityCharges || 0) +
                // (flat.flatMaintananceCharges || 0) +
                // (flat.flatTrashCharges || 0)
              ).toLocaleString()}{" "}
              <Typography variant="caption" color="text.secondary">
                (excluding monthly security, maintenance & trash charges)
              </Typography>
            </Typography>
          </Box>

          {/* Show button or status based on requestStatus */}
          {requestStatus === "pending" ? (
            <Chip
              icon={<HourglassEmptyIcon />}
              label="Request Pending"
              color="warning"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : (requestStatus === "accepted" || requestStatus === "expired") &&
            request?.paymentStatus === "verifying" ? (
            <Chip
              icon={<HourglassEmptyIcon />}
              label="Payment Under Review"
              color="info"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : requestStatus === "accepted" || requestStatus === "expired" ? (
            (() => {
              const overdue = isPastDueDate(request?.dueDate);
              return (
                <Button
                  variant="contained"
                  color={overdue ? "warning" : "success"}
                  size="large"
                  startIcon={overdue ? <ErrorOutlineIcon /> : <PaymentIcon />}
                  onClick={() => {
                    onClose();
                    navigate("/my-requests");
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {overdue ? "Payment Overdue — View Request" : "Make Payment"}
                </Button>
              );
            })()
          ) : requestStatus === "hold" ? (
            <Chip
              icon={<HourglassEmptyIcon />}
              label="On Hold — Another Applicant Is Being Processed"
              color="default"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : requestStatus === "payment_pending" ? (
            <Chip
              icon={<HourglassEmptyIcon />}
              label="Payment Under Review"
              color="info"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : requestStatus === "completed" ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Booking Confirmed"
              color="success"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : requestStatus === "rejected" ||
            requestStatus === "payment_rejected" ? (
            <Chip
              icon={<CancelIcon />}
              label={
                requestStatus === "payment_rejected"
                  ? "Payment Rejected"
                  : "Request Rejected"
              }
              color="error"
              sx={{ px: 2, py: 2.5, fontSize: "1rem" }}
            />
          ) : (
            <Tooltip
              title={isDisabled ? disabledReason : ""}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 320,
                    fontSize: "0.8rem",
                    lineHeight: 1.5,
                    p: 1.5,
                  },
                },
              }}
            >
              <span>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleInterested}
                  disabled={isDisabled}
                  startIcon={isDisabled ? <BlockIcon /> : null}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    color: "#fff",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    },

                    "&.Mui-disabled": {
                      background:
                        "linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)",
                      color: "rgba(255,255,255,0.9)",
                      opacity: 1,
                      pointerEvents: "none",
                    },
                  }}
                >
                  {isDisabled ? "Can't Request" : "I'm Interested"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
